import { allowedChecklistReleaseStatuses } from '../constant';
import { enumToDisplayText } from './format';

export interface TreeNodeItemData {
  id: string;
  text: string;
  items: Array<TreeNodeItemData>;
  selected?: boolean | undefined;
  expanded?: boolean;
}

export interface TreeNode {
  children: Array<TreeNode>;
  disabled: boolean;
  expanded: boolean;
  itemData: TreeNodeItemData;
  items: Array<TreeNode>;
  key: number;
  parent?: TreeNode;
  selected: boolean;
  text: string;
}

export interface FilterItem {
  pkId: string;
  equipmentName?: string;
  name?: string;
  siteName?: string;
  taskName?: string;
  modelName?: string;
  componentName?: string;
  sectionName?: string;
  selected?: boolean;
  items?: Array<any>;
}

export interface UserItem {
  name: string;
  email: string;
  id: number;
  role: string;
  group: string;
  authLevel: string;
  selected?: boolean;
  usersUuid?: string;
}

export interface UserFilters {
  filters: Array<string>;
  activeFilters: Array<{
    name: string;
    email: string;
    id: number;
    role: string;
    group: string;
    authLevel: string;
  }>;
}

interface ActiveFilter {
  name?: string;
  pkId?: string;
  items?: Array<any>;
}

interface Filters {
  filters: Array<FilterItem>;
  activeFilters: Array<ActiveFilter>;
}

interface ChecklistWorkplan {
  pkId: string;
  name: string;
}

interface ChecklistFilter {
  pkId: string;
  releaseStatus: string;
  name: string;
  displayName: string;
  checklistWorkplans: Array<ChecklistWorkplan>;
}

interface ChecklistTreeDataSource {
  id: string;
  text: string;
  pkId?: string;
  items: Array<{
    id: string;
    text: string;
    items: Array<{
      id: string;
      text: string;
    }>;
  }>;
}

export interface ChecklistAndWorkplan {
  checklistUUID: string;
  workplanUUIDs: Array<string>;
}

export const isNodeSelected = (node: TreeNodeItemData): boolean =>
  'selected' in node && (node.selected === true || node.selected === undefined);

export const getSelectedChecklistsAndWorkplans = (
  selectedChecklists: Array<TreeNodeItemData>,
  isNodeSelected: (node: TreeNodeItemData) => boolean,
): Array<ChecklistAndWorkplan> =>
  selectedChecklists.map(node => ({
    checklistUUID: node.id,
    workplanUUIDs: node.items?.filter(isNodeSelected).map(({ id }) => id) ?? [],
  }));

export const groupChecklistFilterByReportStatus = (
  checklistFilter: Array<ChecklistFilter>,
): Array<ChecklistTreeDataSource> => {
  const checklistsByStatus = new Map<string, ChecklistTreeDataSource>();

  checklistFilter.forEach(checklist => {
    const { releaseStatus, checklistWorkplans, pkId, displayName } = checklist;

    if (!allowedChecklistReleaseStatuses.includes(releaseStatus)) {
      return;
    }

    const modifiedChecklist = {
      id: pkId,
      text: displayName,
      items: checklistWorkplans?.map(({ pkId, name }) => ({
        id: pkId,
        text: name,
      })),
    };

    if (checklistsByStatus.has(releaseStatus)) {
      checklistsByStatus.get(releaseStatus)!.items.push(modifiedChecklist);
    } else {
      checklistsByStatus.set(releaseStatus, {
        id: releaseStatus,
        text: enumToDisplayText(releaseStatus),
        items: [modifiedChecklist],
      });
    }
  });

  const modifiedChecklists = Array.from(checklistsByStatus.values());

  const desiredOrder = ['In Progress', 'In Review', 'Approved', 'Archived'];

  // Sort the filterItems based on the desired order
  modifiedChecklists.sort(
    (a, b) => desiredOrder.indexOf(a.text) - desiredOrder.indexOf(b.text),
  );

  return modifiedChecklists;
};

/**
 * Unselects a specific item and its children in a tree structure of checklist items.
 *
 * @param node - The root node of the tree.
 * @param valueId - The ID of the item to unselect.
 * @returns The updated tree node with the specified item and its children unselected.
 */
export const unselectChecklistItemInTree = (
  node: TreeNodeItemData,
  valueId: string,
): TreeNodeItemData => {
  let foundInChildren = false;

  const updatedItems = node.items.map((item: TreeNodeItemData) => {
    if (item.id === valueId) {
      foundInChildren = true;
      return {
        ...item,
        selected: false,
        items: item.items.map((subItem: TreeNodeItemData) => ({
          ...subItem,
          selected: false,
        })),
      };
    } else {
      const updatedItem = {
        ...item,
        items: item.items.map((subItem: TreeNodeItemData) => {
          if (subItem.id === valueId) {
            foundInChildren = true;
            return {
              ...subItem,
              selected: false,
            };
          } else {
            return subItem;
          }
        }),
      };
      return updatedItem;
    }
  });

  return {
    ...node,
    selected: foundInChildren ? false : node.selected,
    items: updatedItems,
  };
};

export const getUserFilterSelectedItems = (
  userItems: Array<UserItem>,
): UserFilters => {
  const selectedUsers = userItems.filter(user => user.selected);

  const userUuids = selectedUsers
    .map(user => user.usersUuid)
    .filter(Boolean) as Array<string>;

  const getList = selectedUsers.map(user => ({
    name: user.name,
    email: user.email,
    id: user.id,
    role: user.role,
    group: user.group,
    authLevel: user.authLevel,
  }));

  return {
    filters: userUuids,
    activeFilters: getList,
  };
};

export const getEquipmentFilterSelectedITems = (
  filterItems: Array<FilterItem>,
): Filters => {
  const tempData: Array<any> = [];
  filterItems.forEach(val => {
    if (val.selected === true) {
      tempData.push(val);
    } else {
      val.items?.forEach(v => {
        if (v.selected === true) {
          tempData.push(v);
        }
      });
    }
  });
  const equipmentUuids: Array<FilterItem> = [];
  tempData.forEach(value => {
    if (value.equipmentName) {
      equipmentUuids.push(value);
    } else {
      value.items.forEach((v: any) => {
        equipmentUuids.push(v);
      });
    }
  });
  const getList: Array<ActiveFilter> = [];
  tempData.forEach(model => {
    if (Object.prototype.hasOwnProperty.call(model, 'siteName')) {
      getList.push({
        name: model.siteName,
        pkId: model.pkId,
        items: model.items,
      });
    } else if (Object.prototype.hasOwnProperty.call(model, 'equipmentName')) {
      let index = -1;
      tempData.forEach(data => {
        if (
          Object.prototype.hasOwnProperty.call(data, 'siteName') &&
          data?.items?.length > 0
        ) {
          data.items.forEach((child: any) => {
            if (child.pkId === model.pkId) {
              index = 0;
            }
          });
        }
      });
      if (index === -1) {
        getList.push({
          name: model.equipmentName,
          pkId: model.pkId,
        });
      }
    }
  });
  return {
    filters: equipmentUuids,
    activeFilters: getList,
  };
};

export const getModelFilterSelectedItems = (
  filterItems: Array<FilterItem>,
): Filters => {
  const tempData: Array<any> = [];
  filterItems.forEach((val): void => {
    if (val.selected === true) {
      tempData.push(val);
    } else {
      val.items?.forEach((v: any): void => {
        if (v.selected === true) {
          tempData.push(v);
        } else {
          v.items.forEach((j: any): void => {
            if (j.selected === true) {
              tempData.push(j);
            }
          });
        }
      });
    }
  });

  const modelComponentUuids: Array<any> = [];
  tempData.forEach(value => {
    if (value.componentName || value.modelName) {
      modelComponentUuids.push(value);
    } else {
      value.items?.forEach((v: any): void => {
        if (v.componentName) {
          modelComponentUuids.push(v);
        } else {
          v.items?.forEach((k: any): void => {
            if (k.componentName) {
              modelComponentUuids.push(v);
            }
          });
        }
      });
    }
  });

  const getList: Array<ActiveFilter> = [];
  tempData.forEach(model => {
    if (Object.prototype.hasOwnProperty.call(model, 'modelName')) {
      getList.push({
        name: model.modelName,
        pkId: model.pkId,
        items: model.items,
      });
    } else if (Object.prototype.hasOwnProperty.call(model, 'assemblyName')) {
      let index = -1;
      tempData.forEach(data => {
        if (
          Object.prototype.hasOwnProperty.call(data, 'modelName') &&
          data?.items?.length > 0
        ) {
          data.items.forEach((child: any) => {
            if (child.pkId === model.pkId) {
              index = 0;
            }
          });
        }
      });
      if (index === -1) {
        getList.push({
          name: model.assemblyName,
          pkId: model.pkId,
          items: model.items,
        });
      }
    } else if (Object.prototype.hasOwnProperty.call(model, 'componentName')) {
      let index = -1;
      tempData.forEach(data => {
        if (
          Object.prototype.hasOwnProperty.call(data, 'modelName') &&
          data?.items?.length > 0
        ) {
          data.items.forEach((child: any) => {
            if (child?.items?.length > 0) {
              child.items.forEach((grant: any) => {
                if (grant.pkId === model.pkId) {
                  index = 0;
                }
              });
            }
          });
        }
        if (
          Object.prototype.hasOwnProperty.call(data, 'assemblyName') &&
          data?.items?.length > 0
        ) {
          data.items.forEach((child: any) => {
            if (child.pkId === model.pkId) {
              index = 0;
            }
          });
        }
      });
      if (index === -1) {
        getList.push({
          name: model.componentName,
          pkId: model.pkId,
        });
      }
    } else {
      // Else
    }
  });

  return {
    filters: modelComponentUuids,
    activeFilters: getList,
  };
};
